<script setup>
import { computed, nextTick, ref, watch, onMounted, onUnmounted } from "vue";
import { clickOutSide as vClickOutSide } from "@mahdikhashan/vue3-click-outside";
import { authLogout } from "@/services/auth";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import Lang from "@/components/elements/Lang.vue";
import InputText from "primevue/inputtext";
import InputGroup from "primevue/inputgroup";
import Button from "primevue/button";
import Avatar from "primevue/avatar";
import Dialog from "primevue/dialog";
import Menu from "primevue/menu";
import { useConfirm } from "primevue/useconfirm";
import ConfirmPopup from "primevue/confirmpopup";
import { useI18n } from "vue-i18n";
import { HasPermission } from "@/utils/permisshoinHelper";
import PermissionsV1 from "@/router/permissions/v1";
import Menubar from "primevue/menubar";
import Sidebar from "primevue/sidebar";
import { useWindowSize, useDebounceFn } from "@vueuse/core";
import useRouterStore from "@/stores/useRouterStore";
import Icon from "@/components/Icon.vue";
import NotificationBell from "@/components/layout/NotificationBell.vue";
import DownloadsBell from "@/components/layout/DownloadsBell.vue";
import { useNotificationStore } from "@/stores/NotificationStore";
import { useDownloadsStore } from "@/stores/DownloadsStore";

const { t } = useI18n();

const routeStore = useRouterStore();

const confirm = useConfirm();
const router = useRouter();
const route = useRoute();
const { width: windowWidth } = useWindowSize();
const isDesktop = computed(() => windowWidth.value >= 1280);

const servicesDropdown = ref(false);
const notificationDropdown = ref(false);
const settingDropdown = ref(false);
const mobileMenuOpen = ref(false);
const auth = useAuthStore();
const routes = computed(() => router.getRoutes());
const headerMenuContainer = ref(null);
const menuMeasureContainer = ref(null);
const moreMeasureRef = ref(null);

const visibleMenuItems = ref([]);
const overflowMenuItems = ref([]);
const MORE_MENU_SLUG = "__more";
const MORE_MENU_LABEL = "more";
const moreMenuText = computed(() => {
  const translated = t("more");
  return translated === MORE_MENU_LABEL ? "Еще" : translated;
});

const notificationStore = useNotificationStore();
const downloadsStore = useDownloadsStore();
let menuResizeObserver = null;

onMounted(() => {
  notificationStore.startCountTimer();
  downloadsStore.startCountTimer();
  menuResizeObserver = new ResizeObserver(() => {
    debouncedRecalculate();
  });
  if (headerMenuContainer.value) {
    menuResizeObserver.observe(headerMenuContainer.value);
  }
  recalculateMenuItems();
});
onUnmounted(() => {
  notificationStore.stopCountTimer();
  downloadsStore.stopCountTimer();
  if (menuResizeObserver) {
    menuResizeObserver.disconnect();
  }
});

const servicesDropdownHide = () => {
  servicesDropdown.value = false;
};
const notificationDropdownHide = () => {
  notificationDropdown.value = false;
};
const settingDropdownHide = () => {
  settingDropdown.value = false;
};

const confirmLogout = (event) => {
  confirm.require({
    target: event.currentTarget,
    message: `${t("really_want_to_go_out")}`,
    icon: "pi pi-question-circle",
    acceptClass: "p-button-danger p-button-sm",
    acceptLabel: t("yes"),
    rejectLabel: t("no"),
    accept: () => {
      logout();
    },
    reject: () => {},
  });
};

const logout = async () => {
  try {
    await authLogout();
    auth.setUserData(null);
    localStorage.removeItem("token");
    await router.push("/login");
  } catch (e) {}
};

watch(
  () => route.path,
  () => {
    servicesDropdownHide();
    notificationDropdownHide();
    settingDropdownHide();
    mobileMenuOpen.value = false;
  },
);

const searchModal = ref(false);
const showSearchModal = () => {
  searchModal.value = true;
};
const hideSearchModal = () => {
  searchModal.value = false;
};

const menu = ref();
const items = ref([
  {
    label: "selection_address",
    icon: "pi pi-map-marker",
    link: "/setting/contest/selection-address",
    type: "link",
    visible: () => HasPermission(PermissionsV1["USERS.ITEM.ADD_SELECTION_CONTACT"]),
  },
  {
    label: "notification-accesses",
    icon: "pi pi-lock",
    link: "/setting/notification-accesses",
    type: "link",
    // visible: () => HasPermission(PermissionsV1["USERS.ITEM.ADD_SELECTION_CONTACT"]),
  },
]);

const toggle = (event) => {
  menu.value.toggle(event);
};

const cogList = computed(() =>
  [
    {
      name: "languages",
      to: "/setting/translations",
      icon: "pi-globe",
      permission: () => HasPermission(PermissionsV1["LANG.GET_LIST"]),
    },
    {
      name: "permissions",
      to: "/permissions",
      icon: "pi-sitemap",
      permission: () => HasPermission(PermissionsV1["AUTH_LEVELS.INDEX"]),
    },
    {
      name: "users",
      to: "/users",
      icon: "pi-users",
      permission: () => HasPermission(PermissionsV1["USERS.INDEX"]),
    },
    {
      name: "skills",
      to: "/skills",
      icon: "pi-share-alt",
      permission: () => HasPermission(PermissionsV1["SKILLS.INDEX"]),
    },
    {
      name: "handbooks",
      to: "/handbooks",
      icon: "pi-list",
      permission: () => HasPermission(PermissionsV1["REFERENCE.INDEX"]),
    },
    {
      name: "portal",
      to: { name: "settings-portal-index" },
      icon: "pi-cog",
      permission: () =>
        HasPermission(PermissionsV1["SETTINGS.SEO.INDEX"]) ||
        HasPermission(PermissionsV1["NEWS.INDEX"]),
    },
    {
      name: "contests",
      to: { name: "setting-contest" },
      icon: "pi-sliders-v",
      permission: () => HasPermission(PermissionsV1["RESERVE.TYPES.INDEX"]),
    },
    {
      name: "analytics",
      to: { name: "analytics" },
      icon: "pi-chart-line",
      permission: () => HasPermission(PermissionsV1["STATISTICS.SELECTION.COUNTS_BY_TYPES"]),
    },
    {
      name: "reports",
      to: { name: "reports" },
      icon: "pi-file-export",
      permission: () => HasPermission(PermissionsV1["REPORTS_PERMISSION"]),
    },
    {
      name: "alerts",
      to: { name: "setting-alerts" },
      icon: "pi-bell",
      permission: () => HasPermission(PermissionsV1["POSITION_RATING_ALERTS.INDEX"]),
    },
  ].filter((i) => i.permission()),
);

const links = computed(() => {
  return routeStore.sortedRoutes.filter((i) => {
    if (!i.to || !i.to?.name) return true;

    const route = routes.value.find((i) => i.name === i.to.name);
    if (!route) return true;
    return true;
  });
});

const menuItems = computed(() => {
  let menu = [];
  menu = links.value.reduce((acc, item) => {
    acc.push(...item.children);
    return acc;
  }, []);
  menu = menu.map((item) => ({
    ...item,
    items: item.children
      ? item.children.map((childrenItem) => ({
          ...childrenItem,
          class: route.path.split("/")[2] === childrenItem?.slug ? "text-blue-500" : "",
          command: childrenItem.children
            ? undefined
            : () => {
                router.push(childrenItem.to ?? `/${item.slug}/${childrenItem.slug}`);
              },
          items: childrenItem.children
            ? childrenItem.children.map((grandChild) => ({
                ...grandChild,
                class:
                  grandChild.to?.query &&
                  Object.entries(grandChild.to.query).every(([k, v]) => route.query[k] == v) &&
                  route.path.split("/")[2] === childrenItem?.slug
                    ? "text-blue-500"
                    : "",
                command: () => {
                  router.push(
                    grandChild.to ?? `/${item.slug}/${childrenItem.slug}/${grandChild.slug}`,
                  );
                },
              }))
            : null,
        }))
      : null,
  }));
  return menu;
});

const adaptiveMenuItems = computed(() => {
  if (!overflowMenuItems.value.length) {
    return visibleMenuItems.value;
  }

  return [
    ...visibleMenuItems.value,
    {
      name: MORE_MENU_LABEL,
      slug: MORE_MENU_SLUG,
      customLabel: moreMenuText.value,
      items: overflowMenuItems.value,
    },
  ];
});

const isActive = (...path) => {
  if ((!route.path || route.path === "/") && !path[0]) return true;
  const rule = "^(\\/)?" + path.join("/") + "(\\/)?(#.+)?(\\?.+)?$";
  const regex = new RegExp(rule);
  return regex.test(route.path);
};

const isMenuItemActive = (slug) => {
  if (!slug) return false;
  return (
    route.path === `/${slug}` ||
    route.path.startsWith(`/${slug}/`) ||
    route.path.startsWith(`/${slug}`)
  );
};

const isAdaptiveMenuItemActive = (item) => {
  if (!item) return false;
  if (item.slug === MORE_MENU_SLUG) {
    return item.items?.some((child) => isMenuItemActive(child.slug));
  }
  return isMenuItemActive(item.slug);
};

const getMenuItemLink = (item) => {
  if (item?.items) return route.fullPath;
  return `/${item.slug}`;
};

const getMenuItemLabel = (item) => {
  if (!item) return "";
  return item.customLabel ?? t(item.name);
};

const recalculateMenuItems = async () => {
  if (!menuItems.value.length) {
    visibleMenuItems.value = [];
    overflowMenuItems.value = [];
    return;
  }

  if (!isDesktop.value) {
    visibleMenuItems.value = menuItems.value;
    overflowMenuItems.value = [];
    return;
  }

  await nextTick();
  await new Promise((r) => requestAnimationFrame(r));

  const containerWidth = headerMenuContainer.value?.clientWidth ?? 0;
  const measureRoot = menuMeasureContainer.value;
  if (!containerWidth || !measureRoot) {
    visibleMenuItems.value = menuItems.value;
    overflowMenuItems.value = [];
    return;
  }

  const widths = menuItems.value.map((item) => {
    const el = measureRoot.querySelector(`[data-menu-slug="${item.slug}"]`);
    return el?.offsetWidth ?? 0;
  });

  const moreWidth = moreMeasureRef.value?.offsetWidth ?? 90;
  const reserveGap = 12;
  const availableWidth = Math.max(containerWidth - reserveGap, 0);

  let usedWidth = 0;
  let splitIndex = menuItems.value.length;

  for (let i = 0; i < widths.length; i += 1) {
    if (usedWidth + widths[i] <= availableWidth) {
      usedWidth += widths[i];
      continue;
    }
    splitIndex = i;
    break;
  }

  if (splitIndex < menuItems.value.length) {
    while (splitIndex > 0 && usedWidth + moreWidth > availableWidth) {
      splitIndex -= 1;
      usedWidth -= widths[splitIndex] ?? 0;
    }
  }

  visibleMenuItems.value = menuItems.value.slice(0, splitIndex);
  overflowMenuItems.value = menuItems.value.slice(splitIndex);
};

const debouncedRecalculate = useDebounceFn(recalculateMenuItems, 50);

watch(
  [menuItems, () => windowWidth.value, isDesktop],
  () => {
    recalculateMenuItems();
  },
  { immediate: true, deep: true },
);
</script>

<template>
  <header
    class="flex justify-between items-center gap-6 w-full bg-white/95 backdrop-blur-md px-6 shadow-[0_2px_15px_rgba(0,0,0,0.03)] border-b border-gray-100 py-1.5 fixed z-50 transition-all"
  >
    <div class="flex-shrink-0">
      <router-link
        to="/"
        class="w-48 xl:w-56 2xl:w-64 flex items-center transition-opacity hover:opacity-80"
      >
        <Icon class="w-full h-8 flex items-center" name="logo__ekyzmat" />
      </router-link>
    </div>
    <div
      v-if="menuItems && isDesktop"
      ref="headerMenuContainer"
      class="min-w-0 flex justify-center"
    >
      <Menubar class="header-menu bg-transparent border-none" :model="adaptiveMenuItems">
        <template #item="slotProps">
          <router-link
            :class="[
              slotProps.props.action.class,
              'rounded-lg hover:bg-gray-50/80 transition-colors mx-1',
            ]"
            :to="getMenuItemLink(slotProps.item)"
          >
            <span
              class="font-medium px-1"
              :class="
                (slotProps.props.label.class,
                slotProps.item.class,
                isAdaptiveMenuItemActive(slotProps.item)
                  ? 'text-blue-600'
                  : 'text-gray-700 hover:text-gray-900')
              "
              >{{ getMenuItemLabel(slotProps.item) }}</span
            >
            <i
              v-if="slotProps.item.items"
              :class="isAdaptiveMenuItemActive(slotProps.item) ? 'text-blue-600' : 'text-gray-400'"
              class="pi pi-chevron-down text-[10px] ml-1.5 opacity-70"
            />
          </router-link>
        </template>
      </Menubar>
      <div ref="menuMeasureContainer" class="header-menu-measure" aria-hidden="true">
        <span
          v-for="item in menuItems"
          :key="`measure-${item.slug}`"
          class="header-menu-measure__item"
          :data-menu-slug="item.slug"
        >
          {{ $t(item.name) }}
          <i v-if="item.items" class="pi pi-chevron-down text-[10px] ml-1.5" />
        </span>
        <span ref="moreMeasureRef" class="header-menu-measure__item" data-menu-slug="__more">
          {{ moreMenuText }}
          <i class="pi pi-chevron-down text-[10px] ml-1.5" />
        </span>
      </div>
    </div>
    <div class="flex items-center justify-end gap-1 shrink-0">
      <Button
        v-if="!isDesktop"
        class="p-2 w-10 h-10 text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center rounded-full"
        text
        @click="mobileMenuOpen = true"
      >
        <i class="pi pi-bars text-xl" />
      </Button>
      <Lang />
      <div class="flex items-center">
        <ul class="flex items-center m-0 p-0 list-none gap-0.5">
          <NotificationBell />
          <DownloadsBell />
          <li class="page-header__nav-item flex items-center justify-center">
            <a
              v-tooltip.bottom="$t('guide')"
              href="https://docs.google.com/document/d/1gZnv3pjLfo3q-fdaevgobVHCYg7qc_hBchUfebyP7dI/edit?tab=t.0"
              target="_blank"
              rel="noopener noreferrer"
              class="relative p-2 w-10 h-10 text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center rounded-full"
            >
              <i class="pi pi-book text-xl" />
              <span
                class="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-blue-500 animate-ping"
              />
              <span class="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-blue-500" />
            </a>
          </li>
          <li
            v-if="cogList.length"
            v-click-out-side="settingDropdownHide"
            class="page-header__nav-item relative flex items-center justify-center"
          >
            <Button
              class="relative p-2 w-10 h-10 text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center rounded-full"
              text
              @click.prevent="settingDropdown = !settingDropdown"
            >
              <i
                class="pi pi-cog text-xl transition-transform duration-300"
                :class="{ 'rotate-90': settingDropdown }"
              ></i>
            </Button>
            <div
              :class="
                settingDropdown
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible -translate-y-2'
              "
              class="absolute top-full right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden transition-all duration-200 origin-top-right z-50"
            >
              <div class="header-dropdown pb-4">
                <div class="px-5 pt-5 pb-3">
                  <div class="flex items-center gap-2 mb-2">
                    <div
                      class="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-500"
                    >
                      <i class="pi pi-cog text-sm"></i>
                    </div>
                    <h4 class="text-base font-bold text-gray-900 m-0 leading-none">
                      {{ $t("settings") }}
                    </h4>
                  </div>
                </div>

                <div class="grid grid-cols-3 gap-2 px-4">
                  <router-link
                    v-for="cog in cogList"
                    :key="cog.name"
                    :to="cog.to"
                    class="flex flex-col gap-2 items-center justify-center no-underline p-3 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors group"
                  >
                    <i
                      :class="cog.icon"
                      class="pi text-2xl text-gray-400 group-hover:text-blue-500 transition-colors"
                    />
                    <span class="text-[11px] font-semibold text-center leading-tight">{{
                      $t(cog.name)
                    }}</span>
                  </router-link>
                </div>
              </div>
            </div>
          </li>
          <li class="page-header__nav-item flex items-center justify-center ml-1">
            <Button
              class="relative p-2 w-10 h-10 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex items-center justify-center rounded-full"
              text
              @click.prevent="confirmLogout($event)"
            >
              <i class="pi pi-sign-out text-lg"></i>
            </Button>
            <ConfirmPopup />
          </li>
        </ul>
      </div>
      <div v-if="auth?.userData" class="pl-2 ml-2 border-l border-gray-100 flex items-center">
        <ul class="m-0 p-0 list-none">
          <li class="relative">
            <Button
              class="p-1 pl-3 pr-2 ml-1 rounded-full hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all flex items-center gap-2"
              text
              type="button"
              @click.prevent="toggle"
            >
              <span
                class="text-sm text-gray-700 font-semibold truncate max-w-[80px] 2xl:max-w-[120px]"
                >{{ auth?.userData?.fname }} {{ auth?.userData?.lname }}</span
              >
              <div v-if="auth?.userData?.photo">
                <Avatar class="border-2 border-white shadow-sm" shape="circle" size="small">
                  <template #default>
                    <img :src="auth?.userData?.photo.path" alt="" class="object-cover bg-white" />
                  </template>
                </Avatar>
              </div>
              <div v-else>
                <Avatar
                  icon="pi pi-user"
                  shape="circle"
                  size="small"
                  class="bg-gray-100 text-gray-500"
                />
              </div>
              <i class="pi pi-angle-down text-gray-400 text-xs ml-0.5" />
            </Button>
            <Menu
              id="overlay_menu"
              ref="menu"
              :model="items"
              :popup="true"
              class="mt-2 w-56 border-none shadow-xl shadow-gray-200/50 rounded-xl overflow-hidden p-1"
            >
              <template #item="slotProps">
                <router-link
                  v-if="slotProps.item.type === 'link'"
                  :class="slotProps.props.action.class"
                  :to="slotProps.item.link"
                  class="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-gray-700 hover:text-blue-600 transition-colors"
                >
                  <i :class="slotProps.item.icon" class="text-gray-400" />
                  <span class="text-sm font-medium">{{ $t(slotProps.item.label) }}</span>
                </router-link>
              </template>
            </Menu>
          </li>
        </ul>
      </div>
    </div>
  </header>
  <Sidebar v-model:visible="mobileMenuOpen" position="left" class="!w-72">
    <template #header>
      <router-link to="/" class="flex items-center">
        <Icon class="h-7" name="logo__ekyzmat" />
      </router-link>
    </template>
    <nav class="flex flex-col gap-0.5 pt-2">
      <template v-for="item in menuItems" :key="item.slug">
        <template v-if="item.items && item.items.length">
          <div class="px-3 pt-3 pb-1 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
            {{ $t(item.name) }}
          </div>
          <router-link
            v-for="child in item.items"
            :key="child.slug"
            :to="child.to ?? `/${item.slug}/${child.slug}`"
            class="flex items-center gap-2.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors no-underline"
            :class="
              isMenuItemActive(child.slug)
                ? 'bg-blue-50 text-blue-600'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
            "
          >
            {{ $t(child.name) }}
          </router-link>
        </template>
        <router-link
          v-else
          :to="`/${item.slug}`"
          class="flex items-center gap-2.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors no-underline"
          :class="
            isMenuItemActive(item.slug)
              ? 'bg-blue-50 text-blue-600'
              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
          "
        >
          {{ $t(item.name) }}
        </router-link>
      </template>
    </nav>
  </Sidebar>
  <Dialog v-model:visible="searchModal" class="w-[700px]" modal>
    <div class="pb-8 min-h-[500px]">
      <form>
        <InputGroup>
          <InputText :placeholder="$t('search')" />
          <Button icon="pi pi-search" />
        </InputGroup>
      </form>
    </div>
  </Dialog>
</template>
<style lang="css">
.p-menubar.header-menu {
  width: 100%;
  padding: 0;
  border: none;
}

.p-menubar.header-menu .p-menubar-root-list {
  flex-wrap: nowrap;
}

.p-menubar.header-menu .p-menubar-root-list .p-menuitem .p-menuitem-content .p-menuitem-link {
  @apply px-3 py-2;
}

.p-menuitem-text {
  @apply text-sm;
}

.header-menu-measure {
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  left: -9999px;
  top: 0;
  white-space: nowrap;
}

.header-menu-measure__item {
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 18px;
}
</style>
